import classNames from "classnames";
import React from "react";
import { deepEqual } from "../deep-equal";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { AppCard, AppCardContent } from "./Generic/AppCard";
import QuestionHeader from "./QuestionHeader";
import { RequiredDocAnswerableQuestionProps } from "./RequiredDocAnswerableQuestion";
import { scrollToViewAfterKeyboardOpen } from "./ScheduledVisitDocScreenUtils";
import { SignatureModal } from "./SignatureModal";

/**
 * MOVED FROM ScheduledVisitDocScreen.tsx
 */

interface ReqDocSignature extends RequiredDocAnswerableQuestionProps {
  type: "Patient" | "Caregiver" | "Custom";
  allowUnsigned: boolean;
}

export class RequiredDocSignature extends React.Component<
  ReqDocSignature,
  { notSigning: boolean; imageData: string | null; answerData: string | null; otherText: string }
> {
  signatureModal: SignatureModal | null;

  constructor(props: ReqDocSignature) {
    super(props);

    const data = this.getAnswerData(props.currentAnswer as string);

    this.state = {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      notSigning: false,
      ...data,
    };

    this.signatureModal = null;
  }

  possibleAnswers = [
    "Patient unable to sign",
    "Patient refused to sign",
    "Verbal consent given by patient/family",
    "Other",
  ];

  shouldComponentUpdate(nextProps: ReqDocSignature, nextState: any) {
    return !deepEqual(nextProps, this.props) || !deepEqual(nextState, this.state);
  }

  onSigEnd = (data: string | null) => {
    console.log("onSigEnd");
    this.setState((_prevState) => {
      this.props.handleOnInsertAnswer(this.props.questionId, data);

      return {
        imageData: data,
        answerData: null,
        otherText: "",
        notSigning: false,
      };
    });
  };

  toggleIsSigning = (newIsNotSigning: boolean) => {
    if (newIsNotSigning) {
      this.refreshSignature(null);
      if (
        this.state.answerData !== null &&
        this.possibleAnswers.indexOf(this.state.answerData) === this.possibleAnswers.length - 1
      ) {
        this.props.handleOnInsertAnswer(this.props.questionId, this.state.otherText);
      } else {
        this.props.handleOnInsertAnswer(this.props.questionId, this.state.answerData);
      }
    } else {
      this.props.handleOnInsertAnswer(this.props.questionId, this.state.imageData);
    }

    this.setState((prevState) => {
      const nextState = {
        notSigning: newIsNotSigning,
        answerData: newIsNotSigning ? prevState.answerData : null,
        imageData: newIsNotSigning ? null : prevState.imageData,
        otherText: newIsNotSigning ? prevState.otherText : "",
      };

      return nextState;
    });
  };

  refreshSignature = (str?: string | null) => {
    if (this.signatureModal !== null) {
      this.signatureModal.refreshImage(str === undefined ? this.state.imageData : str);
    }
  };

  getOtherVal = (ans: string | null) => {
    if (ans === null || this.possibleAnswers.indexOf(ans) !== -1) {
      return "";
    } else {
      return ans;
    }
  };

  getAnswerData = (ans: string | null) => {
    const data = {
      answerData: null as string | null,
      imageData: null as string | null,
      notSigning: false,
      otherText: "",
    };

    if (ans !== null) {
      if (this.isImageString(ans)) {
        data.imageData = ans;
      } else if (
        this.possibleAnswers.slice(0, this.possibleAnswers.length - 1).indexOf(ans) !== -1
      ) {
        data.notSigning = true;
        data.answerData = ans;
      } else {
        data.notSigning = true;
        data.answerData = this.possibleAnswers[this.possibleAnswers.length - 1];
        data.otherText = ans;
      }
    }

    return data;
  };

  isImageString = (str: string): boolean => {
    const regex =
      /^\s*data:([a-z]+\/[a-z]+(;[a-z-]+=[a-z-]+)?)?(;base64)?,[a-z0-9!$&',()*+,;=\-._~:@/?%\s]*\s*$/i;
    const match = str.match(regex);
    return match !== null ? match.length > 0 : false;
  };

  changeNotSigningVal = (answer: string, otherText: string) => {
    const isOtherText = this.possibleAnswers.indexOf(answer) === this.possibleAnswers.length - 1;
    this.setState((prevState) => {
      return {
        otherText: isOtherText ? otherText : prevState.otherText,
        answerData: isOtherText ? this.possibleAnswers[this.possibleAnswers.length - 1] : answer,
      };
    });

    this.props.handleOnInsertAnswer(this.props.questionId, isOtherText ? otherText : answer);
  };

  handleAppCardClick() {
    if (this.props.readonly && this.props.onClickWhileDisabled !== undefined) {
      this.props.onClickWhileDisabled();
    }
  }

  renderPatientSignature() {
    const status = this.state.notSigning;

    return (
      <div>
        <div className="ScheduledVisitDocScreen_patientSignatureNotSignCheckbox ScheduledVisitDocScreen__checkBox checkbox checkbox--material">
          <input
            type="checkbox"
            disabled={this.props.readonly || !this.props.allowUnsigned}
            className="checkbox__input checkbox--material__input"
            checked={status}
            onChange={(event: any) => this.toggleIsSigning(event.target.checked)}
          />
          <div className="checkbox__checkmark checkbox--material__checkmark" />
          {translate(STRINGS.PATIENT_IS_NOT_SIGNING)}
        </div>
        <div
          className={
            "ScheduledVisitDocScreen_patientSignatureNotSignSection " +
            (status ? "ScheduledVisitDocScreen_patientSignatureNotSignSectionShown" : "")
          }
        >
          {this.possibleAnswers.map((val, index) => {
            const isLast = index === this.possibleAnswers.length - 1;
            return (
              <div key={this.props.questionId + "_" + index}>
                <div
                  className={
                    "ScheduledVisitDocScreen__radioButton radio-button radio-button--material " +
                    (isLast ? "ScheduledVisitDocScreen__inlineBlock " : "")
                  }
                >
                  <input
                    className="radio-button__input radio-button--material__input"
                    type="radio"
                    disabled={this.props.readonly || !status}
                    name={`${this.props.questionId.toString()}`} // ${this.state.suppHash}`}
                    onChange={() => {
                      this.changeNotSigningVal(val, this.state.otherText);
                    }}
                    checked={this.state.answerData === val}
                  />
                  <div className="radio-button__checkmark radio-button--material__checkmark" />
                  {val}
                </div>
                {isLast ? (
                  <div
                    onClick={scrollToViewAfterKeyboardOpen}
                    className="ScheduledVisitDocScreen__radioButton"
                  >
                    <input
                      placeholder={translate(STRINGS.ENTER_TEXT_HERE)}
                      className="ScheduledVisitDocScreen__block text-input text-input--material"
                      onChange={(event: any) => {
                        this.changeNotSigningVal(
                          this.possibleAnswers[this.possibleAnswers.length - 1],
                          event.target.value
                        );
                      }}
                      disabled={this.props.readonly}
                      value={this.state.otherText}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderDefaultSignature() {
    const status = this.state.notSigning;

    return (
      <div>
        <div className="ScheduledVisitDocScreen_patientSignatureNotSignCheckbox ScheduledVisitDocScreen__checkBox checkbox checkbox--material">
          <input
            type="checkbox"
            disabled={this.props.readonly || !this.props.allowUnsigned}
            className="checkbox__input checkbox--material__input"
            checked={status}
            onChange={(event: any) => this.toggleIsSigning(event.target.checked)}
          />
          <div className="checkbox__checkmark checkbox--material__checkmark" />
          {translate(STRINGS.NOT_SIGNING)}
        </div>
        <div
          className={
            "ScheduledVisitDocScreen_patientSignatureNotSignSection " +
            (status ? "ScheduledVisitDocScreen_patientSignatureNotSignSectionShown" : "")
          }
        >
          <div onClick={scrollToViewAfterKeyboardOpen}>
            <input
              placeholder={translate(STRINGS.ENTER_TEXT_HERE)}
              className="ScheduledVisitDocScreen__block text-input text-input--material"
              onChange={(event: any) => {
                this.changeNotSigningVal(
                  this.possibleAnswers[this.possibleAnswers.length - 1],
                  event.target.value
                );
              }}
              value={this.state.otherText}
            />
          </div>
        </div>
      </div>
    );
  }

  renderSignature() {
    return this.props.type === "Patient"
      ? this.renderPatientSignature()
      : this.renderDefaultSignature();
  }

  render() {
    return (
      <AppCard
        className={classNames({ __invalid: !this.props.isValid })}
        onClick={() => this.handleAppCardClick()}
      >
        <QuestionHeader
          text={this.props.questionText}
          isRequired={this.props.isRequired}
          notes={this.props.notes}
          isReadonly={this.props.readonly}
        />
        <AppCardContent>
          <SignatureModal
            ref={(ref) => (this.signatureModal = ref)}
            onEnd={this.onSigEnd}
            initialData={this.state.imageData}
            disabled={this.props.readonly}
          />
          {this.props.allowUnsigned ? this.renderSignature() : null}
        </AppCardContent>
      </AppCard>
    );
  }
}
